import _ from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import type { BrandAndProductProps } from '../../components/ExternalBrandLogo';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import LoadingIndicator from '../../components/LoadingIndicator';
import { PlanTier } from '../../graphql/generated/graphql';
import { INSTALLABLE_PLATFORM_INFO_MAP } from '../../integrations/IntegrationPlatformInfo';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import UIActions from '../../ui/UIActions';
import type { Feature } from '../../utils/features';
import { isFeatureEnabled } from '../../utils/features';
import useNavigate from '../../utils/router/useNavigate';
import { Link, useLocation, useRouter } from '../../utils/router/v3Bridge';
import getExternalServiceName from '../../utils/text/getExternalServiceName';
import ExternalServiceCard from '../ExternalServiceCard';
import useIntegrationMap from '../useIntegrationMap';
import styles from './AdminIntegrationView.module.scss';
import installIntegration from './installIntegration';
import MultiFeatureIntegrationModal from './MultiFeatureIntegrationModal';
import RequestIntegration from './RequestIntegration';
import UpcomingIntegrations from './UpcomingIntegrations';
import useDeleteIntegration from './useDeleteIntegration';
import PricingModalButton from '../../organization/PricingModalButton';

const ORDER = ['google', 'slack', 'github'];

const sortPlatforms = <T extends string | { name: string }>(
  platforms: readonly T[],
): T[] => {
  return [...platforms].sort((a, b) => {
    const aName = typeof a === 'string' ? a : a.name;
    const bName = typeof b === 'string' ? b : b.name;

    const aIndex = ORDER.indexOf(aName);
    const bIndex = ORDER.indexOf(bName);

    if (aIndex !== -1) {
      if (bIndex !== -1) {
        return aIndex - bIndex;
      }
      return -1;
    }
    if (bIndex !== -1) {
      return 1;
    }

    return aName.localeCompare(bName);
  });
};

const PLATFORM_ORDER = sortPlatforms([
  'slack',
  'google',
  'github',
  'gitlab',
  'dropbox',
  'jira',
  'asana',
  'clickup',
  'trello',
  'monday',
  'freshdesk',
  'shortcut',
  'height',
  'linear',
  'quip',
  'todoist',
  'bamboohr',
  'wrike',
  'guru',
  'onelogin',
  'okta',
  'microsoft',
  'zendesk',
]);

const UPCOMING_PLATFORM_ORDER = sortPlatforms([
  'productboard',
  'gmail',
  'outlook',
  'salesforce',
  'hubspot',
  'helpscout',
  'intercom',
]);

export const ALL_EMBEDS: {
  name: string;
  brand?: string;
  displayName?: string;
  description: string;
  keys?: string[];
}[] = sortPlatforms([
  { name: 'airtable', description: 'Workspaces' },
  { name: 'drawio', displayName: 'Draw.io', description: 'Diagrams' },
  { name: 'dropbox', description: 'Images and videos' },
  { name: 'figma', description: 'Projects' },
  { name: 'gliffy', description: 'Diagrams' },
  {
    name: 'google',
    displayName: 'Google Workspace',
    description: 'Docs, sheets, slides, calendars, and more',
    keys: [
      'google-calendar',
      'google-doc',
      'google-drawing',
      'google-drive',
      'google-drive-folder',
      'google-form',
      'google-map',
      'google-sheet',
      'google-slide',
    ],
  },
  { name: 'iorad', description: 'Tutorials' },
  {
    name: 'invision',
    displayName: 'InVision',
    description: 'Prototypes',
    keys: ['invision', 'invision-v7'],
  },
  { name: 'jotform', displayName: 'Jotform', description: 'Forms' },
  { name: 'loom', description: 'Screen recordings' },
  {
    name: 'lucidchart',
    displayName: 'Lucid',
    description: 'Charts and boards',
  },
  { name: 'ludus', description: 'Slides' },
  { name: 'miro', description: 'Whiteboards' },
  { name: 'mode', description: 'Dashboards and reports' },
  { name: 'monday', description: 'Board views' },
  {
    name: 'office',
    brand: 'microsoft',
    displayName: 'Office 365',
    description: 'Word, Excel, and PowerPoint',
    keys: ['microsoft-office'],
  },
  { name: 'pingboard', description: 'Charts' },
  { name: 'slideshare', displayName: 'SlideShare', description: 'Slides' },
  {
    name: 'youtube',
    brand: 'google',
    displayName: 'YouTube',
    description: 'Videos',
  },
  { name: 'vimeo', description: 'Videos' },
  {
    name: 'wistia',
    description: 'Videos',
    keys: ['wistia-playlist', 'wistia-video'],
  },
  { name: 'otter', description: 'Conversations' },
  { name: 'typeform', description: 'Forms' },
  { name: 'canva', description: 'Designs' },
  { name: 'smartsheet', description: 'Documents' },
  { name: 'twitter', description: 'Tweets' },
  { name: 'codepen', displayName: 'CodePen', description: 'Pens' },
  { name: 'jsfiddle', displayName: 'JSFiddle', description: 'Fiddles' },
  { name: 'whimsical', displayName: 'Whimsical', description: 'Files' },
  { name: 'pitch', displayName: 'Pitch', description: 'Presentation' },
  { name: 'tableau', displayName: 'Tableau', description: 'Views' },
  { name: 'rows', displayName: 'Rows', description: 'Spreadsheet' },
]);

const AdminIntegrationView = () => {
  const router = useRouter();
  const {
    query: { error },
  } = useLocation();
  const { platform, status: statusOrFeature } = useParams() as {
    platform: string;
    status: string;
  };
  const dispatch = useAppDispatch();
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  ) as PlanTier;
  const isBusinessOrEnterprise = [
    PlanTier.Business,
    PlanTier.Enterprise,
  ].includes(planTier);
  const [integrations, { loading }] = useIntegrationMap({
    // Due to a bug in Apollo 3.7.17 where loading is false when
    // data is present and data is fetched with cache-and-network,
    // we need to use network-only here to avoid an initial loop caused
    // by handleOpenIntegration being called in the useEffect
    // See https://github.com/apollographql/apollo-client/issues/10737
    fetchPolicy: 'network-only',
  });
  const prevLoadingRef = useRef(loading);
  const [deleteIntegration] = useDeleteIntegration();

  let status: 'success' | 'failed' | null = null;
  let feature: string | null = null;

  if (statusOrFeature === 'success' || statusOrFeature === 'failed') {
    status = statusOrFeature;
  } else {
    feature = statusOrFeature;
  }

  useEffect(() => {
    const { name = _.capitalize(platform) } =
      INSTALLABLE_PLATFORM_INFO_MAP[platform] || {};

    switch (status) {
      case 'success':
        dispatch(
          UIActions.pushToast({
            level: 'success',
            message: `${name} has been integrated`,
          }),
        );
        router.replace(`/admin/integrations/${platform}`);
        break;
      case 'failed': {
        const message = `Could not integrate ${name}`;
        const toast = error ? { title: message, message: error } : { message };

        dispatch(UIActions.pushToast({ level: 'error', ...toast }, 10000));
        router.replace('/admin/integrations');
        break;
      }
      default:
        break;
    }
  }, [dispatch, error, platform, router, status]);

  const navigate = useNavigate();
  const handleOpenIntegration = useCallback(
    (p: string) => {
      const integration = integrations[p];
      if (integration && integration.status === 'NORMAL') {
        dispatch(
          UIActions.openModal(MultiFeatureIntegrationModal, { integration }),
        );
      } else if (
        !installIntegration({
          platform: p,
          feature: feature ?? undefined,
          dispatch,
          navigate,
        })
      ) {
        navigate(`/admin/integrations/${p}`);
      }
    },
    [dispatch, integrations, navigate, feature],
  );

  useEffect(() => {
    if (!loading && prevLoadingRef.current && platform) {
      handleOpenIntegration(platform);
    }

    prevLoadingRef.current = loading;
  }, [handleOpenIntegration, loading, platform]);

  const activePlatforms = PLATFORM_ORDER.filter(
    (p) => integrations[p] !== undefined,
  );
  const availablePlatforms = PLATFORM_ORDER.filter(
    (p) => integrations[p] === undefined,
  );

  const renderIntegration = (p: string) => {
    const platformInfo = INSTALLABLE_PLATFORM_INFO_MAP[p];
    if (!platformInfo) {
      return null;
    }
    const { name, icon, description, isPremium } = platformInfo;
    const integration = integrations[p];
    const isPlanUpgradeRequired =
      !integration && isPremium && !isBusinessOrEnterprise;

    // eslint-disable-next-line react/jsx-no-useless-fragment -- Fix next time editing the file
    let action = <></>;

    if (isPlanUpgradeRequired && !isFeatureEnabled(p as Feature)) {
      action = (
        <PricingModalButton feature="integrations">Upgrade</PricingModalButton>
      );
    } else {
      const handlePrimaryAction = () => handleOpenIntegration(p);

      if (!integration) {
        action = (
          <Button variant="secondary" onClick={handlePrimaryAction}>
            Enable
          </Button>
        );
      } else {
        switch (integration.status) {
          case 'ERROR':
            action = (
              <div className={styles.buttonGroup}>
                <Button
                  variant="secondary"
                  danger
                  onClick={() => deleteIntegration(p)}
                >
                  Remove
                </Button>
                <Button variant="primary" onClick={handlePrimaryAction}>
                  Reinstall
                </Button>
              </div>
            );
            break;
          case 'NORMAL':
            action = (
              <Button variant="secondary" onClick={handlePrimaryAction}>
                Modify
              </Button>
            );
            break;
          case 'UPGRADE':
            action = (
              <Button variant="primary" onClick={handlePrimaryAction}>
                Update
              </Button>
            );
            break;
          default:
            break;
        }
      }
    }

    return (
      <ExternalServiceCard
        action={action}
        icon={icon}
        key={p}
        name={name}
        premium={isPremium}
        details={
          <>
            {description}
            {isPlanUpgradeRequired && ' Business subscription required.'}
            {!!integration && integration.status !== 'NORMAL' && (
              <>
                {' '}
                {integration.status === 'UPGRADE' ? (
                  <span className={styles.darkerText}>Update available</span>
                ) : (
                  <span className={styles.dangerText}>Reinstall required</span>
                )}
              </>
            )}
          </>
        }
      />
    );
  };

  return (
    <SettingsContainerView
      title="Integrations"
      subtitle={
        <>
          Connect with other tools your team loves. Learn more about all our
          integrations{' '}
          <Link target="_blank" to="https://go.slab.com/slab-integrations">
            here
          </Link>
          .
        </>
      }
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          {activePlatforms.length > 0 && (
            <SettingsSection header="Active">
              <Cards>{activePlatforms.map(renderIntegration)}</Cards>
            </SettingsSection>
          )}
          {availablePlatforms.length > 0 && (
            <SettingsSection header="Available">
              <Cards>{availablePlatforms.map(renderIntegration)}</Cards>
            </SettingsSection>
          )}
          <SettingsSection header="Embeds">
            <div className={styles.embedsDescription}>
              Paste in a link to one of the following services and they will
              automatically be embedded within Slab. No configuration required.
            </div>
            <div className={styles.embeds}>
              {ALL_EMBEDS.map(({ brand, description, displayName, name }) => (
                <div className={styles.item} key={name}>
                  <ExternalBrandLogo
                    {...({
                      brand: brand || name,
                      product: name,
                    } as BrandAndProductProps)}
                    height={24}
                  />
                  <div className={styles.name}>
                    {displayName || getExternalServiceName(name)}
                  </div>
                  <div className={styles.description}>{description}</div>
                </div>
              ))}
            </div>
          </SettingsSection>
          <SettingsSection header="Upcoming">
            <UpcomingIntegrations platforms={UPCOMING_PLATFORM_ORDER} />
          </SettingsSection>
          <SettingsSection>
            <RequestIntegration />
          </SettingsSection>
        </>
      )}
    </SettingsContainerView>
  );
};

export default AdminIntegrationView;
